import { OSRM_BASE, TRAVEL_TIME_SCALE, TRAVEL_TIME_MIN_SEC, OVER_LIMIT_MPH, distanceKm } from "@/lib/gameConfig";

const MPS_PER_MPH = 0.44704;

// HEMS helicopters fly geodesic (straight line) and ~35% faster than ground
// units. Tweak these two constants together if you want to rebalance air-vs-
// ground response times.
const HEMS_SPEED_MULTIPLIER = 1.35;

const cache = new Map(); // key: from->to rounded + mode, val: { coords:[[lat,lng]], durationSec }

const keyOf = (a, b, mode = "ground") =>
  `${mode}|${a.lat.toFixed(4)},${a.lng.toFixed(4)}|${b.lat.toFixed(4)},${b.lng.toFixed(4)}`;

// Apply "speed limit + OVER_LIMIT_MPH" adjustment then scale for game pacing.
// `realSec` is the OSRM duration (driven at posted speed limits).
// `distanceM` is the route length in meters.
function adjustDuration(realSec, distanceM) {
  if (realSec <= 0 || distanceM <= 0) return TRAVEL_TIME_MIN_SEC;
  const avgMps = distanceM / realSec;           // average posted-limit speed in m/s
  const newMps = avgMps + OVER_LIMIT_MPH * MPS_PER_MPH;
  const newSec = distanceM / newMps;            // duration at limit + N mph
  return Math.max(TRAVEL_TIME_MIN_SEC, newSec * TRAVEL_TIME_SCALE);
}

// Geodesic (great-circle) route — single straight segment, used for HEMS.
// Duration derived from average ground speed at the posted-limit + OVER_LIMIT
// pace, then scaled by the helicopter speed multiplier.
function straightLineRoute(from, to, speedMultiplier = HEMS_SPEED_MULTIPLIER) {
  const km = distanceKm(from, to);
  const distanceM = km * 1000;
  // Reference time: same posted-speed assumption as ground fallback (60 km/h
  // ≈ 37 mph), then sped up by the multiplier so air units arrive faster
  // than ground units routed over the same distance.
  const baseRealSec = (km / 60) * 3600;
  const baseAdjusted = adjustDuration(baseRealSec, distanceM);
  const durationSec = Math.max(TRAVEL_TIME_MIN_SEC, baseAdjusted / Math.max(1, speedMultiplier));
  return {
    coords: [[from.lat, from.lng], [to.lat, to.lng]],
    durationSec,
    fallback: false,
    straightLine: true,
    realDurationSec: baseRealSec,
    distanceMeters: distanceM,
  };
}

const fallbackRoute = (from, to) => {
  const km = distanceKm(from, to);
  const distanceM = km * 1000;
  const realSec = (km / 60) * 3600; // assume avg 60 km/h (~37 mph) posted speed
  return {
    coords: [[from.lat, from.lng], [to.lat, to.lng]],
    durationSec: adjustDuration(realSec, distanceM),
    fallback: true,
    realDurationSec: realSec,
    distanceMeters: distanceM,
  };
};

// `opts`:
//   - apparatusType: if "HEMS", route is a straight line + speed-boosted.
//   - straightLine: explicit override (true → straight line + boost).
//   - speedMultiplier: override the boost factor (defaults to HEMS_SPEED_MULTIPLIER).
export async function getRoute(from, to, opts = {}) {
  const isAir = opts.straightLine || opts.apparatusType === "HEMS";
  if (isAir) {
    const mult = opts.speedMultiplier || HEMS_SPEED_MULTIPLIER;
    const k = keyOf(from, to, `air:${mult}`);
    if (cache.has(k)) return cache.get(k);
    const r = straightLineRoute(from, to, mult);
    cache.set(k, r);
    return r;
  }
  const k = keyOf(from, to, "ground");
  if (cache.has(k)) return cache.get(k);

  try {
    const url = `${OSRM_BASE}/route/v1/driving/${from.lng},${from.lat};${to.lng},${to.lat}?overview=full&geometries=geojson`;
    const ctrl = new AbortController();
    const tid = setTimeout(() => ctrl.abort(), 5000);
    const resp = await fetch(url, { signal: ctrl.signal });
    clearTimeout(tid);
    if (!resp.ok) throw new Error(`OSRM ${resp.status}`);
    const json = await resp.json();
    const route = json.routes && json.routes[0];
    if (!route) throw new Error("no route");
    const coords = route.geometry.coordinates.map(([lng, lat]) => [lat, lng]);
    const realSec = route.duration || 0;
    const distanceM = route.distance || 0;
    const durationSec = adjustDuration(realSec, distanceM);
    const result = {
      coords,
      durationSec,
      fallback: false,
      realDurationSec: realSec,
      distanceMeters: distanceM,
    };
    cache.set(k, result);
    return result;
  } catch (e) {
    const r = fallbackRoute(from, to);
    cache.set(k, r);
    return r;
  }
}

// Position along the multi-point polyline at progress 0..1 by length
export function pointAtProgress(coords, progress) {
  if (!coords || coords.length < 2) return coords[0];
  if (progress <= 0) return coords[0];
  if (progress >= 1) return coords[coords.length - 1];

  // segment lengths
  const segs = [];
  let total = 0;
  for (let i = 1; i < coords.length; i++) {
    const dx = coords[i][0] - coords[i - 1][0];
    const dy = coords[i][1] - coords[i - 1][1];
    const len = Math.sqrt(dx * dx + dy * dy);
    segs.push(len);
    total += len;
  }
  const target = total * progress;
  let acc = 0;
  for (let i = 0; i < segs.length; i++) {
    if (acc + segs[i] >= target) {
      const t = (target - acc) / (segs[i] || 1);
      const a = coords[i];
      const b = coords[i + 1];
      return [a[0] + (b[0] - a[0]) * t, a[1] + (b[1] - a[1]) * t];
    }
    acc += segs[i];
  }
  return coords[coords.length - 1];
}

// Reverse the coordinate list for the return trip
export function reverseRoute(route) {
  return { ...route, coords: [...route.coords].reverse() };
}
